import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Moon, Sun, Flame, LogOut, Zap, Menu, X, ChevronDown, MessageCircle, Users, Mic, Sparkles, Film, Target, User, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import ResetSheet from "@/components/ResetSheet";
import Logo from "@/components/Logo";

const soloLinks = [
  { to: "/app", label: "Today" },
  { to: "/routine", label: "Routine" },
  { to: "/progress", label: "Evolution" },
];

const groups = [
  {
    label: "Coach",
    match: ["/coach", "/practice", "/talk"],
    items: [
      { to: "/coach", label: "Coach", desc: "Live drills & feedback", icon: MessageCircle },
      { to: "/practice", label: "Practice", desc: "Role-play any scenario", icon: Users },
      { to: "/talk", label: "Talk", desc: "Free-flow voice chat", icon: Mic },
    ],
  },
  {
    label: "Reclaim",
    match: ["/inner", "/immersions", "/missions", "/training", "/audio", "/resets"],
    items: [
      { to: "/training", label: "The Diet", desc: "7-day natural attraction", icon: Target, admin: true, parked: true },
      { to: "/inner", label: "Inner", desc: "Inner architecture", icon: Sparkles },
      { to: "/audio", label: "Tonight's Audio", desc: "Personalised night audio", icon: Moon },
      { to: "/immersions", label: "Immerse", desc: "Signature immersions", icon: Film },
      { to: "/missions", label: "Missions", desc: "Real-world missions", icon: Target },
      { to: "/resets", label: "Resets", desc: "60-second state shifts", icon: Wind },
    ],
  },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [reset, setReset] = useState(false);
  const [open, setOpen] = useState(false);
  const streak = user?.profile?.streak || 0;

  const doLogout = async () => { await logout(); navigate("/login"); };
  const isActive = (to) => location.pathname === to;

  return (
    <>
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/app" className="flex items-center" data-testid="nav-logo">
            <Logo className="text-2xl" showDot />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <Link to="/app" data-testid="nav-today"
              className={`px-3.5 py-2 rounded-full text-sm transition-colors duration-300 ${isActive("/app") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>Today</Link>
            <Link to="/routine" data-testid="nav-routine"
              className={`px-3.5 py-2 rounded-full text-sm transition-colors duration-300 ${isActive("/routine") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>Routine</Link>

            {groups.map((g) => {
              const groupActive = g.match.includes(location.pathname);
              return (
                <DropdownMenu key={g.label} modal={false}>
                  <DropdownMenuTrigger data-testid={`nav-group-${g.label.toLowerCase()}`}
                    className={`group flex items-center gap-1 px-3.5 py-2 rounded-full text-sm outline-none transition-colors duration-300 ${groupActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                    {g.label}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" sideOffset={10}
                    className="w-64 p-2 rounded-2xl border border-white/10 bg-popover shadow-2xl">
                    {g.items.filter((it) => !it.parked && (!it.admin || user?.is_admin)).map((it) => (
                      <DropdownMenuItem key={it.to} asChild>
                        <Link to={it.to} data-testid={`nav-${it.label.toLowerCase()}`}
                          className={`flex items-start gap-3 rounded-xl px-3 py-2.5 cursor-pointer transition-colors duration-200 ${isActive(it.to) ? "bg-primary/10 text-primary" : "text-foreground hover:bg-white/5"}`}>
                          <span className="mt-0.5 flex items-center justify-center w-8 h-8 rounded-full bg-primary/12 text-primary shrink-0"><it.icon className="w-4 h-4" /></span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium leading-tight">{it.label}</span>
                            <span className="block text-xs text-muted-foreground truncate">{it.desc}</span>
                          </span>
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}

            <Link to="/progress" data-testid="nav-progress"
              className={`px-3.5 py-2 rounded-full text-sm transition-colors duration-300 ${isActive("/progress") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>Evolution</Link>
            {user?.is_admin && (
              <Link to="/admin" data-testid="nav-admin" className={`px-3.5 py-2 rounded-full text-sm ${isActive("/admin") ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>Admin</Link>
            )}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {user && user.plan !== "premium" && (
              <Button size="sm" onClick={() => navigate("/upgrade")} data-testid="nav-upgrade-btn"
                className="rounded-full bg-gradient-to-r from-blue-500 to-teal-400 text-white shadow-md shadow-blue-500/20 hover:opacity-95 hidden sm:flex">
                <Sparkles className="w-4 h-4 mr-1" /> Upgrade
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setReset(true)} data-testid="need-mirro-btn"
              className="rounded-full text-primary hover:bg-primary/10 hidden sm:flex">
              <Zap className="w-4 h-4 mr-1" /> Need Xcelio
            </Button>
            {streak > 0 && <span className="hidden sm:flex items-center gap-1 text-sm text-primary" data-testid="nav-streak"><Flame className="w-4 h-4" /> {streak}</span>}
            <button onClick={toggle} data-testid="theme-toggle" className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors duration-300">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={() => navigate("/settings")} data-testid="nav-account" className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors duration-300 hidden sm:flex">
              <User className="w-4 h-4" />
            </button>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="nav-logout" className="text-muted-foreground hidden sm:flex"><LogOut className="w-4 h-4" /></Button>
            <button onClick={() => setOpen((o) => !o)} className="lg:hidden p-2 text-foreground" data-testid="nav-mobile-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <nav className="lg:hidden border-t border-border px-5 py-3 flex flex-col gap-1 bg-background/95" data-testid="nav-mobile-panel">
            {soloLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-m-${l.label.toLowerCase()}`}
                className={`px-3 py-2 rounded-lg text-sm ${isActive(l.to) ? "text-primary bg-primary/5" : "text-muted-foreground"}`}>{l.label}</Link>
            ))}
            {groups.map((g) => (
              <div key={g.label} className="pt-2">
                <p className="px-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">{g.label}</p>
                {g.items.filter((it) => !it.parked && (!it.admin || user?.is_admin)).map((it) => (
                  <Link key={it.to} to={it.to} onClick={() => setOpen(false)} data-testid={`nav-m-${it.label.toLowerCase()}`}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm ${isActive(it.to) ? "text-primary bg-primary/5" : "text-muted-foreground"}`}>
                    <it.icon className="w-4 h-4" /> {it.label}
                  </Link>
                ))}
              </div>
            ))}
            {user?.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="mt-2 px-3 py-2 rounded-lg text-sm text-muted-foreground">Admin</Link>}
            {user && user.plan !== "premium" && (
              <button onClick={() => { setOpen(false); navigate("/upgrade"); }} data-testid="nav-m-upgrade"
                className="mt-2 px-3 py-2 rounded-lg text-sm text-white bg-gradient-to-r from-blue-500 to-teal-400 text-left flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Upgrade to Premium
              </button>
            )}
            <button onClick={() => { setOpen(false); setReset(true); }} className="px-3 py-2 rounded-lg text-sm text-primary text-left">Need Xcelio now</button>
            <button onClick={() => { setOpen(false); navigate("/settings"); }} data-testid="nav-m-account" className="px-3 py-2 rounded-lg text-sm text-muted-foreground text-left flex items-center gap-2">
              <User className="w-4 h-4" /> Settings
            </button>
            <button onClick={doLogout} className="px-3 py-2 rounded-lg text-sm text-muted-foreground text-left">Log out</button>
          </nav>
        )}
      </header>
      <ResetSheet open={reset} onClose={() => setReset(false)} />
    </>
  );
}
